import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PiCoinsLight, PiTelegramLogo } from "react-icons/pi";
import { BiCalendar } from "react-icons/bi";
import { FaChevronDown } from "react-icons/fa6";
import { twMerge } from "tailwind-merge";
import moment from "moment-timezone";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import { CustomInput } from "../../../atoms/CustomInput";
import ConfirmModal from "../../../public/ConfirmModal";
import ScheduleControl from "./shared/ScheduleControl";
import { useScheduleField } from "./shared/useScheduleField";
import { datetimeLocalSchema, scheduleJoi } from "./shared/scheduleDateTime";
import {
  useSendBulkSMS,
  useSendSMS,
  useSendUsersBulkSMS,
} from "../../../../hooks/messages";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import parse from "html-react-parser";
import { useGetEntityOwnerData } from "../../../../hooks/entity";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import NoTranslate from "../../../atoms/NoTranslate";

interface SendSMSModalProps {
  show: boolean;
  onClose: () => void;
  identification: string;
  userName?: string;
  userPhone?: string;
  onAddCredit: () => void;
  isBulkMessage?: boolean;
  users?: Array<string>;
  recipientCount?: number;
  onResolved?: () => void;
  isSmsBlocked?: boolean;
}

interface SMSFormData {
  message: string;
  isScheduled: boolean;
  scheduledDateTime?: string;
}

const SMS_SEGMENT_LENGTH = 160;
const MAX_SMS_LENGTH = 310;
const MIN_SMS_LENGTH = 10;

const isUsCanadaPhone = (phone?: string): boolean => {
  if (!phone) return false;
  const cleaned = phone.replace(/[\s().-]/g, "");
  return cleaned.startsWith("+1") || cleaned.startsWith("001");
};

const SendSMSModal: React.FC<SendSMSModalProps> = ({
  show,
  onClose,
  identification,
  userName,
  userPhone,
  onAddCredit,
  isBulkMessage,
  users,
  recipientCount,
  onResolved,
  isSmsBlocked,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customer_identification, user } = useAuth();
  const permissions = usePermissions(user);
  const { mutate: sendSMS, isLoading } = useSendSMS();
  const { mutate: sendBulkSMS, isLoading: isSendingBulk } = useSendBulkSMS();
  const { mutate: sendUsersBulkSMS, isLoading: isSendingUsersBulk } =
    useSendUsersBulkSMS();
  const { data: entityOwnerData } = useGetEntityOwnerData(
    customer_identification,
    user.entity_identification,
    "sms_credit"
  );

  const [pendingBulk, setPendingBulk] = useState<SMSFormData | null>(null);

  const schema = useMemo(
    () =>
      scheduleJoi.object({
        message: scheduleJoi
          .string()
          .min(MIN_SMS_LENGTH)
          .max(MAX_SMS_LENGTH)
          .required()
          .messages({
            "string.min": t(
              "dashboard.entities.viewEntityUser.sendSms.messageTooShort",
              {
                minLength: MIN_SMS_LENGTH,
              }
            ) as string,
            "string.max": t(
              "dashboard.entities.viewEntityUser.sendSms.characterLimitExceeded"
            ) as string,
            "string.empty": "Message is required",
          }),
        isScheduled: scheduleJoi.boolean(),
        scheduledDateTime: scheduleJoi.when("isScheduled", {
          is: true,
          then: datetimeLocalSchema()
            .required()
            .messages({
              "string.empty": t(
                "dashboard.entities.viewEntityUser.sendSms.selectDateTime"
              ) as string,
              "any.required": t(
                "dashboard.entities.viewEntityUser.sendSms.selectDateTime"
              ) as string,
              "datetimeLocal.min": t(
                "dashboard.entities.viewEntityUser.sendSms.invalidScheduleTime"
              ) as string,
              "datetimeLocal.max": t(
                "dashboard.entities.viewEntityUser.sendSms.invalidScheduleTime"
              ) as string,
            }),
          otherwise: scheduleJoi.string().allow("").optional(),
        }),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<SMSFormData>({
    resolver: joiResolver(schema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      message: "",
      isScheduled: false,
      scheduledDateTime: "",
    },
  });

  const message = watch("message");
  const isScheduled = watch("isScheduled");
  const scheduledDateTime = watch("scheduledDateTime");

  const schedule = useScheduleField({
    isOpen: show,
    armed: Boolean(isScheduled),
    value: scheduledDateTime ?? "",
    setArmed: (armed) =>
      setValue("isScheduled", armed, {
        shouldValidate: true,
        shouldDirty: false,
      }),
    setValue: (value) =>
      setValue("scheduledDateTime", value, {
        shouldValidate: true,
        shouldDirty: false,
      }),
  });

  const availableSmsCredits =
    entityOwnerData?.entity_other_data?.sms_credit ?? 0;

  const smsCreditsRequired = useMemo(() => {
    if (!message || message.length === 0) return 0;
    return message.length > SMS_SEGMENT_LENGTH ? 2 : 1;
  }, [message]);

  const hasEnoughCredits = availableSmsCredits >= smsCreditsRequired;

  const bulkRecipientCount =
    users && users.length > 0 ? users.length : recipientCount ?? 0;

  useEffect(() => {
    if (!show) {
      reset();
      setPendingBulk(null);
    }
  }, [show, reset]);

  const isSending = isLoading || isSendingBulk || isSendingUsersBulk;

  const usCanadaBlocked = !isBulkMessage && isUsCanadaPhone(userPhone);

  const openLargeEnterpriseSupport = () => {
    onClose();
    navigate("/control-panel/support?requestType=large_enterprise_request");
  };

  const performSend = (data: SMSFormData) => {
    const scheduledDate = schedule.getScheduledDate();
    const scheduledTime = scheduledDate
      ? scheduledDate.toISOString()
      : undefined;

    if (isBulkMessage) {
      if (users && users.length > 0) {
        sendUsersBulkSMS(
          {
            customer_identification,
            entity_identification: user.entity_identification,
            group_identification: identification,
            data: {
              message: data.message.trim(),
              users: users,
              ...(scheduledTime && { schedule: scheduledTime }),
            },
          },
          {
            onSuccess: () => {
              onClose();
              onResolved?.();
            },
          }
        );
      } else {
        sendBulkSMS(
          {
            customer_identification,
            entity_identification: user.entity_identification,
            group_identification: identification,
            data: {
              message: data.message.trim(),
              ...(scheduledTime && { schedule: scheduledTime }),
            },
          },
          {
            onSuccess: () => {
              onClose();
              onResolved?.();
            },
          }
        );
      }
    } else {
      sendSMS(
        {
          customer_identification,
          entity_identification: user.entity_identification,
          user_identification: identification,
          data: {
            message: data.message.trim(),
            ...(scheduledTime && { schedule: scheduledTime }),
          },
        },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    }
  };

  const onSubmit = (data: SMSFormData) => {
    if (isBulkMessage && data.isScheduled) {
      setPendingBulk(data);
      return;
    }
    performSend(data);
  };

  const remainingChars = message
    ? MAX_SMS_LENGTH - message.length
    : MAX_SMS_LENGTH;
  const canSend =
    !isSmsBlocked &&
    !usCanadaBlocked &&
    !errors.message &&
    !errors.scheduledDateTime &&
    !schedule.isIncomplete &&
    message &&
    message.trim().length >= MIN_SMS_LENGTH &&
    message.length <= MAX_SMS_LENGTH &&
    hasEnoughCredits &&
    smsCreditsRequired > 0 &&
    permissions.can(Cap.SEND_SMS, Level.WRITE);

  const scheduleTooltip = schedule.isIncomplete
    ? (t(
        "dashboard.entities.viewEntityUser.sendMsg.scheduleIncompleteHint"
      ) as string)
    : undefined;

  return (
    <>
      <ModalShell
        show={show}
        onClose={onClose}
        size="xl"
        title={t("dashboard.entities.viewEntityUser.sendSms.title")}
        subtitle={
          userName ? (
            <NoTranslate>
              {users &&
                users?.length > 0 &&
                t("dashboard.entities.viewEntityUser.sendSms.selectedUsersOf")}
              {userName}
              {userPhone && (
                <span className="text-gray-400"> ({userPhone})</span>
              )}
            </NoTranslate>
          ) : undefined
        }
        onSubmit={handleSubmit(onSubmit)}
        footer={
          <div className="flex w-full flex-col gap-3">
            {schedule.armed && (
              <ScheduleControl
                id="sms-schedule-panel"
                schedule={schedule}
                control={control}
                valueName="scheduledDateTime"
                error={errors.scheduledDateTime}
              />
            )}
            <div className="flex flex-wrap items-center justify-end gap-3">
              <CustomButton
                variant="secondary"
                onClick={onClose}
                disabled={isSending}
                title={t("common.cancel") as string}
              />

              <div className="flex items-stretch">
                <CustomButton
                  type="submit"
                  variant="primary"
                  isLoading={isSending}
                  disabled={isSending || !canSend}
                  className="rounded-r-none"
                  tooltip={scheduleTooltip}
                  comp={
                    schedule.armed ? (
                      <div className="flex items-center gap-1 sm:gap-2">
                        <BiCalendar size={16} className="text-white" />
                        <span>
                          {t(
                            "dashboard.entities.viewEntityUser.sendSms.schedule"
                          )}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 sm:gap-2">
                        <PiTelegramLogo size={16} className="text-white" />
                        <span>
                          {t("dashboard.entities.viewEntityUser.sendSms.send")}
                        </span>
                      </div>
                    )
                  }
                />

                <CustomButton
                  type="button"
                  variant="primary"
                  onClick={schedule.toggle}
                  disabled={isSending}
                  className="rounded-l-none border-l border-blue-500 px-2"
                  aria-expanded={schedule.armed}
                  aria-controls="sms-schedule-panel"
                  aria-label={
                    t(
                      "dashboard.entities.viewEntityUser.sendMsg.buttons.scheduleOption"
                    ) as string
                  }
                  tooltip={
                    t(
                      "dashboard.entities.viewEntityUser.sendMsg.buttons.scheduleOption"
                    ) as string
                  }
                  comp={
                    <FaChevronDown
                      size={14}
                      className={twMerge(
                        "text-white transition-transform",
                        schedule.armed && "rotate-180"
                      )}
                    />
                  }
                />
              </div>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {isSmsBlocked && (
            <div className="rounded-lg bg-red-50 p-3 border border-red-200">
              <p className="text-sm text-red-800 font-medium">
                {t(
                  "dashboard.entities.viewEntityUser.sendSms.communicationBlocked"
                )}
              </p>
            </div>
          )}

          {usCanadaBlocked && (
            <div className="rounded-lg bg-red-50 p-3 border border-red-200">
              <p className="text-sm text-red-800">
                {t(
                  "dashboard.entities.viewEntityUser.sendSms.usCanadaNotAvailable"
                )}{" "}
                <button
                  type="button"
                  onClick={openLargeEnterpriseSupport}
                  className="font-medium text-blue-600 underline"
                >
                  {t(
                    "dashboard.entities.viewEntityUser.sendSms.contactSupport"
                  )}
                </button>
              </p>
            </div>
          )}

          <div className="rounded-lg bg-amber-50 p-3 border border-amber-200">
            <p className="text-sm text-amber-800">
              {parse(
                t(
                  "dashboard.entities.viewEntityUser.sendSms.unidirectionalExplanation"
                )
              )}
            </p>
          </div>

          <CustomInput
            name="message"
            control={control}
            label={
              t(
                "dashboard.entities.viewEntityUser.sendSms.messageLabel"
              ) as string
            }
            placeholder={
              t(
                "dashboard.entities.viewEntityUser.sendSms.messagePlaceholder"
              ) as string
            }
            rows={5}
            maxLength={310}
            error={errors.message}
            helperText={errors.message}
            hint={
              <CharacterCounter
                currentLength={message.length}
                maxLength={MAX_SMS_LENGTH}
                className={`font-medium ${
                  message && message.length >= MAX_SMS_LENGTH
                    ? "text-red-600"
                    : remainingChars < 30
                    ? "text-amber-400"
                    : "text-gray-600"
                }`}
              />
            }
          />

          <div
            className={`rounded-lg p-3 border ${
              hasEnoughCredits
                ? "bg-blue-50 border-blue-100"
                : "bg-red-50 border-red-200"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1">
                <PiCoinsLight
                  className={`w-4 h-4 flex-shrink-0 ${
                    hasEnoughCredits ? "text-blue-600" : "text-red-600"
                  }`}
                />
                <div className="text-xs">
                  <p
                    className={
                      hasEnoughCredits ? "text-blue-800" : "text-red-800"
                    }
                  >
                    {t(
                      "dashboard.entities.viewEntityUser.sendSms.creditsAvailable",
                      {
                        credits: availableSmsCredits,
                      }
                    )}
                  </p>
                  {smsCreditsRequired > 0 && (
                    <p
                      className={`font-medium mt-0.5 ${
                        hasEnoughCredits ? "text-blue-900" : "text-red-900"
                      }`}
                    >
                      {t(
                        "dashboard.entities.viewEntityUser.sendSms.creditsRequired",
                        {
                          credits: smsCreditsRequired,
                        }
                      )}
                    </p>
                  )}
                  {!hasEnoughCredits && smsCreditsRequired > 0 && (
                    <p className="text-red-700 font-medium mt-1">
                      {t(
                        "dashboard.entities.viewEntityUser.sendSms.insufficientCredits",
                        {
                          needed: smsCreditsRequired - availableSmsCredits,
                        }
                      )}
                    </p>
                  )}
                </div>
              </div>
              <CustomButton
                onClick={() => {
                  onAddCredit();
                }}
                variant="outline"
                title={t(
                  "dashboard.entities.viewEntityUser.sendSms.chargeSmsCredit"
                )}
              />
            </div>
          </div>
        </div>
      </ModalShell>

      <ConfirmModal
        show={Boolean(pendingBulk)}
        onClose={() => setPendingBulk(null)}
        title={
          t(
            "dashboard.entities.viewEntityUser.sendSms.confirmSchedule.title"
          ) as string
        }
        description={
          <div className="space-y-1 text-left">
            <p>
              {t(
                "dashboard.entities.viewEntityUser.sendSms.confirmSchedule.recipients",
                { count: bulkRecipientCount }
              )}
            </p>
            <p>
              {t(
                "dashboard.entities.viewEntityUser.sendSms.confirmSchedule.when",
                {
                  when: `${moment(schedule.value).format("lll")} (${
                    schedule.timezone
                  })`,
                }
              )}
            </p>
            <p className="font-medium text-gray-700">
              {t(
                "dashboard.entities.viewEntityUser.sendSms.confirmSchedule.cost",
                { credits: smsCreditsRequired * bulkRecipientCount }
              )}
            </p>
          </div>
        }
        cancelBtnName={t("common.cancel") as string}
        cancelBtnFn={() => setPendingBulk(null)}
        confirmBtnName={
          t(
            "dashboard.entities.viewEntityUser.sendSms.confirmSchedule.confirm"
          ) as string
        }
        confirmBtnFn={() => {
          const data = pendingBulk;
          setPendingBulk(null);
          if (data) performSend(data);
        }}
        isLoading={isSending}
      />
    </>
  );
};

export default SendSMSModal;
